import type { JSX } from 'react';
import type { WireChatMessage } from '@logistics/contracts';
import { ago } from '@/lib/format';
import { now } from '@/lib/clock';
import { ArtifactBody } from './artifacts/artifact-body';
import {
  artifactGlyph,
  artifactKindLabel,
  artifactRetrievedAt,
  artifactSource,
  artifactTitle,
} from './artifacts/artifact-meta';

/**
 * The right-hand panel: the artifact of one answer. There is no "refresh" in
 * the footer — re-running a retrieval from here is deferred (ADR 0001).
 */
export function ArtifactPanel({
  message,
  onClose,
}: {
  message: WireChatMessage;
  onClose: () => void;
}): JSX.Element | null {
  const artifact = message.artifact;
  if (artifact === undefined) return null;
  const retrieved = artifactRetrievedAt(message.citations) ?? message.createdAt;
  return (
    <aside
      aria-label="Artifact"
      className="border-line bg-bg animate-drawer flex h-full min-h-0 min-w-0 flex-col border-l"
    >
      <div className="border-line flex flex-none items-center gap-3 border-b px-[18px] py-3.5">
        <span className="bg-acc-soft text-acc grid size-8 flex-none place-items-center rounded-[9px] font-mono text-[10px] font-medium">
          {artifactGlyph(artifact)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{artifactTitle(artifact)}</div>
          <div className="text-mute truncate font-mono text-[10.5px] tracking-[.06em]">
            {artifactKindLabel(artifact)} · {artifactSource(message.citations)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close artifact"
          className="border-line text-mute hover:text-ink size-[30px] flex-none rounded-lg border bg-transparent text-[15px]"
        >
          ×
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-[18px]">
        <ArtifactBody artifact={artifact} />
      </div>
      <div className="border-line text-mute flex flex-none items-center justify-between border-t px-[18px] py-2.5 font-mono text-[10.5px]">
        <span>retrieved {ago(retrieved, now())}</span>
      </div>
    </aside>
  );
}
